import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out

  useEffect(() => {
    // CRITICAL: returning from Google OAuth, AuthCallback exchanges the session_id first —
    // checking /auth/me now would 401 before the session exists.
    if (window.location.hash?.includes("session_id=")) return;
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  const login = useCallback(async (email, password, extra = {}) => {
    const res = await api.post("/auth/login", { email, password, ...extra });
    if (res.data.token) localStorage.setItem("ht_token", res.data.token);
    setUser(res.data);
    return res.data;
  }, []);

  const register = useCallback(async (payload) => {
    const res = await api.post("/auth/register", payload);
    if (res.data.verification_required) return res.data;  // no session until the email is verified
    if (res.data.token) localStorage.setItem("ht_token", res.data.token);
    setUser(res.data);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* logout request is best-effort — local state is cleared regardless */
    }
    localStorage.removeItem("ht_token");
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  }, []);

  const value = useMemo(() => ({ user, login, register, logout, refreshUser }),
    [user, login, register, logout, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
